import { useState } from "react";
import { Breadcrumb } from "./Breadcrumb.js";

type Props = {
  language: string | undefined;
};

export function ComingSoon({ language }: Props) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Default to french if language is undefined
  const safeLanguage = language || 'fr';

  // Map language codes to names for display
  const languageNameMap: Record<string, string> = {
    'fr': 'French',
    'it': 'Italian',
    'ca': 'Catalan',
    'ro': 'Romanian'
  };

  // Map languages to their flag emojis and colors
  const languageThemeMap: Record<string, { flag: string; color: string; gradient: string }> = {
    'fr': { 
      flag: '🇫🇷', 
      color: 'blue', 
      gradient: 'from-blue-500 to-blue-600' 
    },
    'it': { 
      flag: '🇮🇹', 
      color: 'green', 
      gradient: 'from-green-500 to-green-600' 
    },
    'ca': { 
      flag: '🏴󠁥󠁳󠁣󠁴󠁿', 
      color: 'red', 
      gradient: 'from-red-500 to-red-600' 
    },
    'ro': { 
      flag: '🇷🇴', 
      color: 'yellow', 
      gradient: 'from-yellow-500 to-yellow-600' 
    }
  };

  const theme = languageThemeMap[safeLanguage] || languageThemeMap['fr'];
  const languageName = languageNameMap[safeLanguage] || 'Language';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isLoading) return;

    setIsLoading(true);
    
    try {
      const res = await fetch('/api/data/waitlist', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, lang: safeLanguage }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting email:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <Breadcrumb 
        items={[{ name: "Home", url: `/learn-${safeLanguage === 'fr' ? 'french' : safeLanguage === 'it' ? 'italian' : safeLanguage === 'ca' ? 'catalan' : safeLanguage === 'ro' ? 'romanian' : 'french'}` }]} 
        currentPage={`${languageName} - Coming Soon`} 
      />
      
      <div className="text-center py-6 sm:py-8">
        {/* Flag and Title */}
        <div className="mb-6">
          <div className="text-4xl sm:text-5xl mb-3">{theme.flag}</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {languageName} is Coming Soon!
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            We're working hard to bring you the best {languageName.toLowerCase()} learning experience.
          </p>
        </div>

        {/* Coming Soon Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 sm:p-6 mb-6">
          <div className="mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Be the first to know when it's ready
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Get notified as soon as {languageName} vocabulary, grammar, and conversation practice becomes available.
            </p>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="max-w-sm mx-auto">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className={`px-4 py-2 rounded-lg bg-gradient-to-r ${theme.gradient} text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity text-sm`}
                >
                  {isLoading ? 'Sending...' : 'Notify Me'}
                </button>
              </div>
            </form>
          ) : (
            <div className="max-w-sm mx-auto">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <div className="text-green-600 dark:text-green-400">✓</div>
                  <div>
                    <p className="text-green-800 dark:text-green-200 font-medium text-sm">
                      You're all set!
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-xs">
                      We'll notify you when {languageName} is ready.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Features Preview */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            What to expect
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-start gap-2">
              <div className="text-blue-500 mt-0.5">📚</div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">Vocabulary Practice</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  CEFR-level vocabulary with spaced repetition
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="text-green-500 mt-0.5">📝</div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">Grammar Exercises</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Verb conjugations and sentence structure
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="text-purple-500 mt-0.5">💬</div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">Speaking Practice</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Connect with native {languageName.toLowerCase()} tutors
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="text-orange-500 mt-0.5">🎧</div>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm">Comprehension</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Listening and reading practice materials
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Available Languages */}
        <div className="mt-6">
          <p className="text-gray-600 dark:text-gray-400 mb-3 text-sm">
            In the meantime, try our other languages:
          </p>
          <div className="flex justify-center gap-2">
            <a
              href="/learn-spanish"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm"
            >
              🇪🇸 Spanish
            </a>
            <a
              href="/learn-portuguese"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm"
            >
              🇵🇹 Portuguese
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
